import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { validatePromoCode } from 'patient-api-lib'
import { PreconditionError } from '@buf/dentechse_patient-api-gateway.grpc_web/api/patient/v1/request_failure_pb'
import i18n from '../../util/i18n'
import { AppDispatch, RootState } from '../../store'
import {
  sendInvalidPromoCode,
  sendValidPromoCode,
} from '../../util/messageHandler'
import { rudderstackErrors } from '../../constants/app'

export type PromoCodeState = {
  validPromoCodeValue: string | undefined
  promoCodeStatus: 'rejected' | 'fulfilled' | 'pending' | 'idle'
  promoCodeErrorMessage: string | undefined
  originalPrice: number | undefined
  finalPrice: number | undefined
}

export const initialState: PromoCodeState = {
  validPromoCodeValue: undefined,
  promoCodeStatus: 'idle',
  promoCodeErrorMessage: undefined,
  originalPrice: undefined,
  finalPrice: undefined,
}

export const checkPromoCode = createAsyncThunk<
  { finalPrice: number; originalPrice: number },
  string,
  { dispatch: AppDispatch; state: RootState }
>('promoCode/checkPromoCode', async (promoCode, thunkApi) => {
  const state = thunkApi.getState()
  const clinicId = state.clinicScreen.selectedClinic?.id
  const treatmentId = state.appointmentScreen.selectedAppointment?.treatmentId
  const originalPrice =
    state.appointmentScreen.selectedAppointment?.price?.standardPrice
  const appointmentDateTime = new Date(
    state.appointmentScreen.selectedAppointment?.startTime || ''
  )
  const {
    FINAL_OR_ORIGINAL_PRICE_IS_UNDEFINED,
    FINAL_PRICE_IS_GREATER_THAN_REDUCED_PRICE,
    REDUCED_PRICE_IS_UNDEFINED,
    UNKNOWN_RESPONSE_ERROR,
  } = rudderstackErrors

  if (
    !clinicId ||
    !treatmentId ||
    !appointmentDateTime ||
    !originalPrice ||
    !promoCode
  ) {
    return thunkApi.rejectWithValue(
      i18n.t('validations:promoCodeValidation.required')
    )
  }

  try {
    const res = await validatePromoCode(
      promoCode,
      clinicId,
      treatmentId,
      originalPrice,
      appointmentDateTime
    )

    const failure = res.getFailure()
    const success = res.getSuccess()

    if (failure) {
      if (
        failure.getPreconditionError() ===
        PreconditionError.PRECONDITION_ERROR_INVALID_PROMO_CODE
      ) {
        sendInvalidPromoCode(promoCode, failure.getMessage()?.getValue() || '')
        return thunkApi.rejectWithValue(
          i18n.t('validations:promoCodeValidation.invalid', { promoCode })
        )
      }

      sendInvalidPromoCode(promoCode, failure.getMessage()?.getValue() || '')
      return thunkApi.rejectWithValue(
        i18n.t('validations:promoCodeValidation.failed')
      )
    }

    if (success) {
      const finalPrice = success.getFinalPrice()?.getUnits()
      const originalPrice = success.getOriginalPrice()?.getUnits()
      const reducedPrice =
        state.appointmentScreen.selectedAppointment?.price?.reducedPrice

      if (!finalPrice || !originalPrice) {
        sendInvalidPromoCode(promoCode, FINAL_OR_ORIGINAL_PRICE_IS_UNDEFINED)
        return thunkApi.rejectWithValue(
          i18n.t('validations:promoCodeValidation.error')
        )
      }

      if (!reducedPrice) {
        sendInvalidPromoCode(promoCode, REDUCED_PRICE_IS_UNDEFINED)
        return thunkApi.rejectWithValue(
          i18n.t('validations:promoCodeValidation.error')
        )
      }

      if (reducedPrice <= finalPrice) {
        sendInvalidPromoCode(
          promoCode,
          FINAL_PRICE_IS_GREATER_THAN_REDUCED_PRICE
        )
        return thunkApi.rejectWithValue(
          i18n.t('validations:promoCodeValidation.alreadyReducedPrice')
        )
      }

      sendValidPromoCode(promoCode, finalPrice, originalPrice)
      return {
        finalPrice: finalPrice,
        originalPrice: originalPrice,
      }
    }

    sendInvalidPromoCode(promoCode, UNKNOWN_RESPONSE_ERROR)
    return thunkApi.rejectWithValue(
      i18n.t('validations:promoCodeValidation.error')
    )
  } catch (error) {
    if (error instanceof Error) {
      sendInvalidPromoCode(promoCode, error.message)
    }
    return thunkApi.rejectWithValue(
      i18n.t('validations:promoCodeValidation.error')
    )
  }
})

export const promoCodeSlice = createSlice({
  name: 'promoCode',
  initialState,
  reducers: {
    setPromoCodeValue: (state, action: PayloadAction<string>) => {
      state.validPromoCodeValue = action.payload
    },
    resetPromoCodePrices: (state) => {
      state.originalPrice = undefined
      state.finalPrice = undefined
    },
    clearPromoErrors: (state) => {
      state.promoCodeErrorMessage = undefined
      state.promoCodeStatus = 'idle'
    },
    clearState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(checkPromoCode.pending, (state) => {
      state.promoCodeStatus = 'pending'
    })
    builder.addCase(checkPromoCode.fulfilled, (state, action) => {
      state.promoCodeStatus = 'fulfilled'
      state.validPromoCodeValue = action.meta.arg
      state.finalPrice = action.payload.finalPrice
      state.originalPrice = action.payload.originalPrice
    })
    builder.addCase(checkPromoCode.rejected, (state, action) => {
      state.promoCodeStatus = 'rejected'
      state.promoCodeErrorMessage = action.payload as string
    })
  },
})

export const getPromoCodeStatus = (state: RootState) =>
  state.promoCode.promoCodeStatus

export const getOriginalPrice = (state: RootState) =>
  state.promoCode.originalPrice

export const getFinalPrice = (state: RootState) => state.promoCode.finalPrice

export const getPromoCodeErrorMessage = (state: RootState) =>
  state.promoCode.promoCodeErrorMessage

export const getValidPromoCodeValue = (state: RootState) =>
  state.promoCode.validPromoCodeValue

export const {
  setPromoCodeValue,
  clearPromoErrors,
  resetPromoCodePrices,
  clearState,
} = promoCodeSlice.actions

export const promoCodeReducer = promoCodeSlice.reducer
